import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Loader2, CheckCircle2, AlertCircle, Plus, Trash2, Shuffle, MapPin, HelpCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import OnboardingTour from './OnboardingTour';
import {
  getRuralExchangeVacancies,
  getMyRuralExchangeRequest,
  submitRuralExchangeRequest,
  getCurrentAssignments,
  updateUserFlag,
} from '../services/api';
import '../styles/RuralExchange.css';

// チュートリアルステップ定義
const TOUR_STEPS = [
  {
    target: '[data-tour="rex-header"]',
    title: '地域実習交換ページ',
    content: 'このページでは、地域実習の配属先を他の空き枠と交換する希望を提出できます。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="rex-current-assignments"]',
    title: '現在の地域実習配属',
    content: '抽選前の自分の地域実習配属状況を確認できます。どのクールにどの病院・科に配属されているかが表示されます。',
    placement: 'bottom',
  },
  {
    target: '[data-tour="rex-vacancy-matrix"]',
    title: '空き枠マトリクス',
    content: '病院×科×タームごとの空き枠を一覧で確認できます。各セルの「追加」ボタンで直接希望に追加できます。',
    placement: 'top',
  },
  {
    target: '[data-tour="rex-vacancy-stats"]',
    title: '空き枠統計',
    content: '全体の空き枠数と残席合計を確認できます。',
    placement: 'left',
  },
  {
    target: '[data-tour="rex-form-section"]',
    title: '希望の提出',
    content: 'ここで交換希望を登録します。空き枠を選択し、優先度を設定して提出してください。',
    placement: 'top',
  },
  {
    target: '[data-tour="rex-form-row"]',
    title: '希望行',
    content: '空き枠を選択し、優先度（数字が小さいほど優先）を設定します。複数の希望を登録する場合は「行を追加」で追加できます。',
    placement: 'top',
  },
  {
    target: '[data-tour="rex-submit-button"]',
    title: '希望を提出',
    content: '設定が完了したら、このボタンで希望を提出します。',
    placement: 'top',
  },
  {
    target: '[data-tour="rex-snapshot"]',
    title: '提出済みの内容',
    content: '提出済みの希望内容を確認できます。優先度順に表示されます。',
    placement: 'top',
  },
];

const buildEmptyRow = (nextPriority = 1) => ({
  target_department_term_capacity_id: '',
  priority: nextPriority,
});

const TERM_COLUMNS = [1, 2, 3, 4, 5, 6, 7];

const RuralExchangePage = () => {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vacancies, setVacancies] = useState([]);
  const [formRows, setFormRows] = useState([buildEmptyRow()]);
  const [requestSnapshot, setRequestSnapshot] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [currentRuralAssignments, setCurrentRuralAssignments] = useState([]);
  const [showTour, setShowTour] = useState(false);

  // ツアー完了時のハンドラー
  const handleTourComplete = () => {
    setShowTour(false);
  };

  // ガイドボタンクリック時のハンドラー
  const handleGuideClick = () => {
    setShowTour(true);
  };

  const sortedVacancies = useMemo(() => {
    return [...(vacancies || [])].sort((a, b) => {
      if (a.institution_id !== b.institution_id) return a.institution_id - b.institution_id;
      if (a.term_id !== b.term_id) return a.term_id - b.term_id;
      return (a.department_id || 0) - (b.department_id || 0);
    });
  }, [vacancies]);

  const groupedVacancies = useMemo(() => {
    const map = new Map();
    sortedVacancies.forEach((v) => {
      const [hospitalNameRaw, departmentNameRaw] = (v.display_name || '').split(' - ');
      const hospitalName = (hospitalNameRaw || v.display_name || '').trim();
      const departmentName = (departmentNameRaw || '').trim();
      const key = `${v.institution_id || ''}-${v.department_id || ''}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          institution_id: v.institution_id,
          department_id: v.department_id,
          hospitalName,
          departmentName,
          terms: {},
        });
      }
      map.get(key).terms[v.term_id] = { ...v, hospitalName, departmentName };
    });

    return Array.from(map.values()).sort((a, b) => {
      if ((a.institution_id || 0) !== (b.institution_id || 0)) {
        return (a.institution_id || 0) - (b.institution_id || 0);
      }
      return (a.department_id || 0) - (b.department_id || 0);
    });
  }, [sortedVacancies]);

  const vacancyMap = useMemo(() => {
    return sortedVacancies.reduce((acc, v) => {
      acc[v.department_term_capacity_id] = v;
      return acc;
    }, {});
  }, [sortedVacancies]);

  const totalOpenSeats = useMemo(
    () => sortedVacancies.reduce((sum, v) => sum + (v.vacancies || 0), 0),
    [sortedVacancies]
  );
  const openVacancyCount = useMemo(
    () => sortedVacancies.filter((v) => (v.vacancies || 0) > 0).length,
    [sortedVacancies]
  );

  useEffect(() => {
    if (!authLoading) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  const load = async () => {
    setLoading(true);
    setError('');
    setFeedback('');
    try {
      const [vacancyData, existing, assignments] = await Promise.all([
        getRuralExchangeVacancies(),
        getMyRuralExchangeRequest(),
        getCurrentAssignments(),
      ]);
      setVacancies(vacancyData || []);
      const assignmentList = assignments || [];
      const ruralExchangeAssignments = assignmentList.filter(
        (a) => a.assignment_stage === 'rural-exchange'
      );
      const ruralAssignments = ruralExchangeAssignments.length
        ? ruralExchangeAssignments
        : assignmentList.filter((a) => a.assignment_stage === 'rural');
      setCurrentRuralAssignments(ruralAssignments);
      if (existing && existing.details) {
        setRequestSnapshot(existing);
        const details = (existing.details || []).map((d) => ({
          target_department_term_capacity_id: d.target_department_term_capacity_id,
          priority: d.priority,
        }));
        setFormRows(details.length ? details : [buildEmptyRow()]);
      } else {
        setRequestSnapshot(null);
        setFormRows([buildEmptyRow()]);
      }
    } catch (err) {
      const message = err?.response?.data?.detail || err.message || 'データの取得に失敗しました';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (index, field, value) => {
    setFormRows((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, [field]: field === 'priority' ? Number(value) : value } : row
      )
    );
  };

  const addRow = () => {
    const maxPriority =
      formRows.reduce((max, row) => Math.max(max, Number(row.priority) || 0), 0) || 0;
    setFormRows((prev) => [...prev, buildEmptyRow(maxPriority + 1)]);
  };

  const removeRow = (index) => {
    setFormRows((prev) => prev.filter((_, i) => i !== index));
  };

  const addFromVacancy = (capacityId) => {
    if (formRows.some((r) => String(r.target_department_term_capacity_id) === String(capacityId))) {
      setFeedback('既にリストに追加されています');
      return;
    }
    const maxPriority =
      formRows.reduce((max, row) => Math.max(max, Number(row.priority) || 0), 0) || 0;
    setFormRows((prev) => [...prev, { target_department_term_capacity_id: capacityId, priority: maxPriority + 1 }]);
    setFeedback('リストに追加しました。優先度は後から調整できます');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback('');
    setError('');

    const cleaned = formRows
      .filter((row) => row.target_department_term_capacity_id)
      .map((row) => ({
        target_department_term_capacity_id: Number(row.target_department_term_capacity_id),
        priority: Number(row.priority),
      }));

    // 空配列での提出も許可（希望を取り消す場合）
    if (cleaned.length > 0) {
      const prioritySet = new Set();
      const targetSet = new Set();
      for (const detail of cleaned) {
        if (!vacancyMap[detail.target_department_term_capacity_id]) {
          setError('有効な枠のみ選択できます');
          return;
        }
        if (prioritySet.has(detail.priority)) {
          setError('優先度が重複しています。異なる番号を設定してください');
          return;
        }
        if (targetSet.has(detail.target_department_term_capacity_id)) {
          setError('同じ枠が複数回指定されています');
          return;
        }
        prioritySet.add(detail.priority);
        targetSet.add(detail.target_department_term_capacity_id);
      }
    }

    const payload = cleaned.sort((a, b) => a.priority - b.priority);

    try {
      setSaving(true);
      const saved = await submitRuralExchangeRequest(payload);
      if (user && user.uid) {
        await updateUserFlag(user.uid, 'rural_exchange_preference_status', true);
      }
      setRequestSnapshot(saved);
      const nextRows = (saved?.details || []).map((d) => ({
        target_department_term_capacity_id: d.target_department_term_capacity_id,
        priority: d.priority,
      }));
      setFormRows(nextRows.length ? nextRows : []);
      setFeedback(payload.length > 0 
        ? '提出が完了しました。結果は抽選後に反映されます。'
        : '希望を取り消しました。');
    } catch (err) {
      const message = err?.response?.data?.detail || err.message || '提出に失敗しました';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rex-loading">
        <LoadingSpinner text="読み込み中..." />
      </div>
    );
  }

  return (
    <div className="rex-page">
      <OnboardingTour
        steps={TOUR_STEPS}
        tourId="rural-exchange-page-tour"
        autoStart={showTour}
        onComplete={handleTourComplete}
      />
      <header className="rex-header" data-tour="rex-header">
        <div>
          <p className="rex-kicker">Rural Exchange</p>
          <h1>地域実習配属交換</h1>
          <p className="rex-subtitle">
            空いている地域実習枠に抽選で移動申請できます。優先度を付けて提出してください。
          </p>
        </div>
        <div className="rex-actions">
          <button type="button" className="rex-button ghost" onClick={load}>
            <RefreshCw size={18} />
            最新の空き枠を再取得
          </button>
          <button
            type="button"
            className="rex-button guide-button"
            onClick={handleGuideClick}
            title="操作ガイドを表示"
          >
            <HelpCircle size={18} />
            ガイド
          </button>
        </div>
      </header>

      {error && (
        <div className="rex-alert danger">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      <div className="rex-grid">
        <section className="rex-card" data-tour="rex-current-assignments">
          <div className="rex-card-header">
            <div>
              <h2>現在の地域実習配属</h2>
              <p className="rex-card-subtitle">抽選前の自分の配属状況を確認できます</p>
            </div>
          </div>
          {currentRuralAssignments && currentRuralAssignments.length > 0 ? (
            <div className="rex-request-list">
              {currentRuralAssignments
                .slice()
                .sort((a, b) => (a.term_id || 0) - (b.term_id || 0))
                .map((assignment) => (
                  <div
                    key={assignment.assignment_id || `${assignment.term_id}-${assignment.department_term_capacity_id}`}
                    className="rex-request-item"
                  >
                    <div className="rex-chip">
                      <MapPin size={16} />
                      <span>第{assignment.term_id}クール</span>
                    </div>
                    <div className="rex-request-body">
                      <strong>{assignment.institution_name} - {assignment.department_name}</strong>
                      <div className="rex-request-meta">
                        <span>配属ID: {assignment.assignment_id}</span>
                        {assignment.department_term_capacity_id && (
                          <span>枠ID: {assignment.department_term_capacity_id}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <p className="rex-empty">地域実習配属がまだ登録されていません。</p>
          )}
        </section>

        <section className="rex-card" data-tour="rex-vacancy-matrix">
          <div className="rex-card-header">
            <div>
              <h2>空き枠マトリクス</h2>
              <p className="rex-card-subtitle">病院×科×タームで空き枠を直接追加できます</p>
            </div>
            <div className="rex-stats" data-tour="rex-vacancy-stats">
              <div className="rex-stat">
                <span className="rex-stat-label">空き枠数</span>
                <strong>{openVacancyCount}</strong>
              </div>
              <div className="rex-stat">
                <span className="rex-stat-label">残席合計</span>
                <strong>{totalOpenSeats}</strong>
              </div>
            </div>
          </div>
          <div className="rex-matrix-wrapper">
            <div className="rex-matrix">
              <div className="rex-matrix-head">
                <div className="rex-matrix-cell head">病院名</div>
                <div className="rex-matrix-cell head">科名</div>
                {TERM_COLUMNS.map((term) => (
                  <div key={term} className="rex-matrix-cell head term-head">
                    ターム{term}
                  </div>
                ))}
              </div>
              <div className="rex-matrix-body">
                {groupedVacancies.length === 0 && <p className="rex-empty">現在、空き枠はありません。</p>}
                {groupedVacancies.map((row) => (
                  <div className="rex-matrix-row" key={row.key}>
                    <div className="rex-matrix-cell sticky">{row.hospitalName || '病院名未登録'}</div>
                    <div className="rex-matrix-cell sticky">{row.departmentName || '科未登録'}</div>
                    {TERM_COLUMNS.map((term) => {
                      const entry = row.terms[term];
                      if (!entry) {
                        return (
                          <div key={term} className="rex-matrix-cell empty">
                            -
                          </div>
                        );
                      }
                      const vacancyCount = entry.vacancies || 0;
                      const vacancyBadgeClass =
                        vacancyCount === 0 ? 'rex-badge zero' : vacancyCount === 1 ? 'rex-badge low' : 'rex-badge';
                      return (
                        <div key={term} className="rex-matrix-cell">
                          <div className="rex-matrix-vacancy">
                            <span className={vacancyBadgeClass}>{entry.vacancies} 席</span>
                            <small className="rex-muted">
                              定員 {entry.capacity} / 現在 {entry.current_assignments}
                            </small>
                          </div>
                          <button
                            type="button"
                            className="rex-button tiny"
                            onClick={() => addFromVacancy(entry.department_term_capacity_id)}
                          >
                            <Plus size={14} />
                            追加
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="rex-card" data-tour="rex-form-section">
          <div className="rex-card-header">
            <div>
              <h2>希望の提出</h2>
              <p className="rex-card-subtitle">優先度を付けて空き枠を申請</p>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="rex-form">
            <div className="rex-form-rows">
              {formRows.length === 0 ? (
                <p className="rex-empty">希望がありません。「行を追加」ボタンで希望を追加してください。</p>
              ) : (
                formRows.map((row, index) => (
                  <div className="rex-form-row" key={index} {...(index === 0 ? { 'data-tour': 'rex-form-row' } : {})}>
                    <div className="rex-form-field">
                      <label>空き枠</label>
                      <select
                        value={row.target_department_term_capacity_id}
                        onChange={(e) => updateRow(index, 'target_department_term_capacity_id', e.target.value)}
                      >
                        <option value="">選択してください</option>
                        {sortedVacancies.map((v) => (
                          <option key={v.department_term_capacity_id} value={v.department_term_capacity_id}>
                            {`[T${v.term_id}] ${v.display_name} / 残 ${v.vacancies}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="rex-form-field small">
                      <label>優先度</label>
                      <input
                        type="number"
                        min="1"
                        value={row.priority}
                        onChange={(e) => updateRow(index, 'priority', e.target.value)}
                      />
                  </div>
                  <div className="rex-form-actions">
                    <button type="button" className="rex-icon-button" onClick={() => removeRow(index)} aria-label="行を削除">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                ))
              )}
            </div>
            <div className="rex-form-footer">
              <button type="button" className="rex-button ghost" onClick={addRow}>
                <Plus size={16} />
                行を追加
              </button>
              <button type="submit" className="rex-button primary" disabled={saving} data-tour="rex-submit-button">
                {saving ? (
                  <>
                    <Loader2 className="spin" size={16} /> 送信中…
                  </>
                ) : (
                  <>
                    <Shuffle size={16} /> 希望を提出
                  </>
                )}
              </button>
            </div>
            {feedback && (
              <div className="rex-alert success" style={{ marginTop: '1rem' }}>
                <CheckCircle2 size={18} />
                <span>{feedback}</span>
              </div>
            )}
          </form>
        </section>

        <section className="rex-card" data-tour="rex-snapshot">
          <div className="rex-card-header">
            <div>
              <h2>提出済みの内容</h2>
              <p className="rex-card-subtitle">現在登録されている希望のスナップショット</p>
            </div>
          </div>
          {requestSnapshot && requestSnapshot.details && requestSnapshot.details.length > 0 ? (
            <div className="rex-request-list">
              {requestSnapshot.details
                .slice()
                .sort((a, b) => a.priority - b.priority)
                .map((detail) => (
                  <div key={detail.id || `${detail.priority}-${detail.target_department_term_capacity_id}`} className="rex-request-item">
                    <div className="rex-chip">
                      <Shuffle size={16} />
                      <span>優先度 {detail.priority}</span>
                    </div>
                    <div className="rex-request-body">
                      <strong>{detail.display_name || '選択中の枠'}</strong>
                      <div className="rex-request-meta">
                        <span>Term {detail.term_id}</span>
                        {typeof detail.vacancy === 'number' && <span>残 {detail.vacancy}</span>}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <p className="rex-empty">提出済みの希望はまだありません。</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default RuralExchangePage;
